import { useCallback } from "react";
import {
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Checkbox,
    Chip,
    Tooltip
} from "@mui/material";

import "./style.scss";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import {
    GroupedInvitedUser,
    KBRoleAssignment
} from "../../../common/interfaces/admin/user-with-kb-roles.interface";

const MAX_VISIBLE_CHIPS = 3;

const KBChips = ({ knowledgeBases }: { knowledgeBases: KBRoleAssignment[] }) => {
    const visible = knowledgeBases.slice(0, MAX_VISIBLE_CHIPS);
    const remaining = knowledgeBases.length - MAX_VISIBLE_CHIPS;

    return (
        <div className="um-kb-chips">
            {visible.map(kb => (
                <Chip
                    key={kb.knowledgeBaseUid}
                    label={`${kb.knowledgeBaseDisplayName} (${kb.role})`}
                    size="small"
                    className="um-kb-chips__chip"
                />
            ))}
            {remaining > 0 && (
                <Tooltip
                    title={knowledgeBases
                        .slice(MAX_VISIBLE_CHIPS)
                        .map(kb => `${kb.knowledgeBaseDisplayName} (${kb.role})`)
                        .join(", ")}
                >
                    <Chip
                        label={`+${remaining} more`}
                        size="small"
                        className="um-kb-chips__chip um-kb-chips__chip--more"
                    />
                </Tooltip>
            )}
        </div>
    );
};

export const InvitedUsersTable = ({
    list,
    checkedItems,
    setCheckedItems,
    handleResend
}: {
    list: GroupedInvitedUser[];
    checkedItems: GroupedInvitedUser[];
    setCheckedItems: (items: GroupedInvitedUser[]) => void;
    handleResend: (item: GroupedInvitedUser) => void;
}) => {
    const isSelectedItem = useCallback(
        (user: GroupedInvitedUser) => {
            return checkedItems.some(item => item.email === user.email);
        },
        [checkedItems]
    );

    const handleChangeSelect = useCallback(
        (item: GroupedInvitedUser) => {
            const exists = checkedItems.find(ci => ci.email === item.email);
            if (exists) {
                setCheckedItems(checkedItems.filter(ci => ci.email !== item.email));
            } else {
                setCheckedItems([...checkedItems, item]);
            }
        },
        [checkedItems, setCheckedItems]
    );

    const handleChangeSelectAll = useCallback(() => {
        setCheckedItems(list.length === checkedItems.length ? [] : [...list]);
    }, [list, checkedItems, setCheckedItems]);

    return (
        <div className="um-table-view">
            <TableContainer>
                <Table stickyHeader className="um-table">
                    <TableHead className="um-table-header">
                        <TableRow className="um-table-header-row">
                            <TableCell className="um-table-header-cell um-table-header-cell--checkbox">
                                <span className="visually-hidden">Select</span>
                                <Checkbox
                                    checked={list.length > 0 && list.length === checkedItems.length}
                                    onChange={handleChangeSelectAll}
                                    icon={<CheckboxIcon />}
                                    checkedIcon={<CheckboxCheckedIcon />}
                                    inputProps={{ "aria-label": "Select all users" }}
                                />
                            </TableCell>
                            <TableCell className="um-table-header-cell">
                                <span className="um-table-header-cell__text">Email</span>
                            </TableCell>
                            <TableCell className="um-table-header-cell um-table-header-cell--kb">
                                <span className="um-table-header-cell__text">Assistants</span>
                            </TableCell>
                            <TableCell className="um-table-header-cell um-table-header-cell--resend">
                                <span className="um-table-header-cell__text"></span>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className="um-table-body">
                        {list.map(item => (
                            <TableRow key={item.email} className="um-table-row">
                                <TableCell className="um-table-cell um-table-cell--checkbox">
                                    <Checkbox
                                        checked={isSelectedItem(item)}
                                        onChange={() => handleChangeSelect(item)}
                                        icon={<CheckboxIcon />}
                                        checkedIcon={<CheckboxCheckedIcon />}
                                        inputProps={{ "aria-label": `Select ${item.email}` }}
                                    />
                                </TableCell>
                                <TableCell className="um-table-cell">
                                    <span className="um-table-cell__text">{item.email}</span>
                                </TableCell>
                                <TableCell className="um-table-cell um-table-cell--kb">
                                    <KBChips knowledgeBases={item.knowledgeBases} />
                                </TableCell>
                                <TableCell className="um-table-cell um-table-cell--resend">
                                    <Button
                                        className="secondary-button"
                                        variant="text"
                                        aria-label={`Resend invite for ${item.email}`}
                                        onClick={() => handleResend(item)}
                                    >
                                        Resend
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};
